import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductShareRefs
 * @property {HTMLButtonElement} shareButton - The icon-only share control
 * @property {HTMLElement} [shareStatus] - Announces the clipboard fallback
 */

/**
 * Shares the current product.
 *
 * Hands off to the platform share sheet where there is one (every touch device
 * and Safari), and copies the URL otherwise, which is the only fallback that
 * works without a third-party endpoint.
 *
 * @extends {Component<ProductShareRefs>}
 */
export class ProductShareComponent extends Component {
  requiredRefs = ['shareButton'];

  /** @type {number | undefined} */
  #timer;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  async share() {
    const url = this.dataset.shareUrl || window.location.href;
    const title = this.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // A dismissed share sheet is not a failure, so it must not fall through to the clipboard
        if (error instanceof Error && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#announce();
    } catch (error) {
      console.error(error);
    }
  }

  /** Shows the "link copied" line, then takes it back out of the accessibility tree. */
  #announce() {
    const { shareStatus } = this.refs;
    if (!shareStatus) return;

    shareStatus.classList.remove('visually-hidden');
    shareStatus.classList.add('product-share__status--visible');

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      shareStatus.classList.add('visually-hidden');
      shareStatus.classList.remove('product-share__status--visible');
    }, 2400);
  }
}

if (!customElements.get('product-share-component')) {
  customElements.define('product-share-component', ProductShareComponent);
}
